import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './worksheet-options.hbs'
import FormView from 'scoreboard/components/form-view'
import renderSheetIndexSelect from 'scoreboard/imports/render-sheet-index-select'
import i18n from 'scoreboard/components/i18n'
import formHelper from 'scoreboard/components/form-helper'
import DataPreviewPanel from 'scoreboard/components/data-preview-panel'
import { checkboxTransform } from 'checked-input-wrapper'
import OnOffToggler from 'scoreboard/components/on-off-toggle'
import Dataset from 'scoreboard/datasets/model/dataset'
import Import from 'scoreboard/imports/model/import'
import formatValue from 'scoreboard/components/format-value'
import * as d3 from 'd3-selection'
import { handleMissingColumnsException } from 'scoreboard/imports/new-column-name-chooser-wizard'
import resizeDialogContent from 'scoreboard/components/resize-dialog-content'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO } = DataType
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'

const WorksheetOptions = Backbone.View.extend({

	tagName: 'form',

	className: 'worksheet-options',

	events: {
		'change [name=sheetIndex]': 'refreshData',
		'click .show-all': 'refreshData',
		'change input[type=checkbox].use-row': 'onCheckboxChange',
		'click .cancel': 'close'
	},

	/**
	 * Step in the wizard that shows:
	 * <ul>
	 * <li>a dropdown for choosing the worksheet
	 * <li>example data from the worksheet
	 * <li>"Use" checkboxes which for certain rows in the spreadsheet to be
	 * ignored.
	 *
	 * <p>Used both in the new dataset
	 * wizard and the import metrics wizard.
	 *
	 * @constructs
	 *
	 * @param {Object} [options]
	 *
	 * @param {Dataset} [options.model] the dataset to be built by the wizard
	 *
	 * @param {Object} [options.dataSourceMetadata] if specified, will read
	 * metadata from here instead of the model
	 */
	initialize: function (options) {
		this.options = options || {}
		var WorksheetOptionsFormView = FormView.extend({
			test: this.test.bind(this)
		})

		this.formView = new WorksheetOptionsFormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: () => _.isNil(this.test())
		})

		this.toggles = []
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		this.options.wizard.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})

		let metadata = this.model.get('dataSourceMetadata')
		renderSheetIndexSelect(this.el, metadata.sheets, metadata.sheetIndex)
		this.sheetIndexField = new MarkedField({
			el: this.$('[name=sheetIndex]'),
			validator: requiredValidator
		}).render()

		formHelper.writeToDom(this.model, this.el)

		this.renderPreviewPanel()

		// #24572 show all link
		this.toggleShowAll()

		this.formView.start()

		// #21881 display any validation message for initially selected sheet
		// #42188 must go after this.formView.start()
		if (metadata.messageCode) {
			this.formView.errorMessage(i18n.get(metadata.messageCode))
		}

		return this
	},

	resize(maxHeight) {
		let previewPanel =  this.$('.preview-panel')
		resizeDialogContent({
			dialog: this.el,
			maxHeight,
			target: previewPanel.find('.panel'), // Scrollable element
			minHeight: 300,
			container: previewPanel[0],
			setMaxHeight: true
		})
	},

	/**
	 * @param [preview] the preview data to render. If not specified, uses the
	 * preview data from the model. If the preview data is empty, renders a blank
	 * table.
	 */
	renderPreviewPanel: function (
		preview = this.model?.get('preview') ?? {},
	) {
		// remove any existing preview
		_.invoke(this.dataPreviewPanel, 'remove')
		this.dataPreviewPanel = new DataPreviewPanel({
			label: this.context().panelTitle,
			preview,
			formatValues: _.get(this.options, 'formatValues', true)

		}).render()
		this.$('.preview-panel').append(this.dataPreviewPanel.el)

		// #31019 'header row' th
		let headerRow = '<thead><tr>' +
			'<th>' + _.escape(i18n.get('admin.import.worksheetOptions.headerRow')) + '</th>'
		// #32146 ignore column th (only for Imports)
		if (this.model instanceof Import) {
			headerRow += `<th>${_.escape(i18n.get('admin.import.transform.ignore'))}</th>`
		}
		_.each(preview.columns, column =>
			headerRow += `<th>${_.escape(column.label)}</th>`)
		headerRow += '</tr></thead>'
		this.$('tbody').before(headerRow)

		// determine the index of the header row, if any (#31019)
		let headerIndex = this.model.headerRowIndex()
		// #32146 ignoreRows (for Imports only)
		let ignoreRows = this.model.getDataSourceMetadata('ignoreRows') || []

		_.each(this.$('tbody tr'), (el, index) => {
			// 'ignored' is for rows occurring before the selected header
			// #32146 it is possible for imports to not have a header
			const ignored = _.isNil(headerIndex) ? false : index < headerIndex
			let toggled = index == headerIndex
			// #32146 ignore column checkbox (only for Imports)
			// Imports may have ignored rows after the selected header
			let ignoreRow = _.includes(ignoreRows, index)
			if (this.model instanceof Import) {
				const checked = ignoreRow ? 'checked' : ''
				const disabled = ignored || toggled ? 'disabled' : ''
				let checkbox = checkboxTransform(`<input type="checkbox" class="use-row" value="${index}" ${checked} ${disabled}>`)
				let checkboxHtml = `<td><label class="checkbox-label">${checkbox}</label></td>`
				this.$(el).prepend(checkboxHtml)
			}
			// #31019 toggles for selecting header row
			let toggle = new OnOffToggler({
				name: `toggle${index}`,
				checked: toggled,
				id: index
			})
			this.toggles.push(toggle)
			toggle.on('change', (val, e) => this.onHeaderToggleChange(val, toggle, e))

			this.$(el).prepend(`<td data-id="${index}"></td>`)
			this.$(`td[data-id=${index}]`).html(toggle.el)
			toggle.render()


			this.$(el).data('id', index)
			if (ignored || ignoreRow) {
				this.$(el).addClass('is-ignored')
			}
		})

		// #21759 hide column headings
		this.$('table thead tr:first').addClass('is-not-displayed')

		// if a header row is already selected, toggle the row color
		let row = this.$(`td[data-id=${headerIndex}]`).closest('tr')
		this.toggleHeaderRowBackground(row, true)
		// #33709 don't format header row cells that were parsed as yes-no
		this.toggleYesNoFormattingForHeaderRow(row, true)
	},

	/**
	 * When an 'ignore row' checkbox is clicked (available only for Imports)
	 * adjust the background color of the row
	 */
	onCheckboxChange: function(e) {
		let cb = $(e.target)
		let row = cb.closest('tr')
		let ignored = cb.prop('checked')
		row.toggleClass('is-ignored', ignored)
	},

	/**
	 * When a "header row" toggle is changed
	 */
	onHeaderToggleChange: function(val, toggle, e) {
		this.clearPreviousHeader()
		if (val) {
			// selecting any toggle deselects all of the others
			_.each(this.toggles, t => {
				if (t !== toggle) {
					t.val(false)
				}
			})
			let row = toggle.$el.closest('tr')
			this.toggleHeaderRowBackground(row, val)
			this.toggleYesNoFormattingForHeaderRow(row, val)
		} else {
			// deselecting a toggle will select the previous toggle
			let id = toggle.options.id
			if (!_.isNil(id)) {
				let prevId = id - 1
				let prevToggle = _.find(this.toggles,
					t => t.options.id === prevId)
				prevToggle && prevToggle.toggle()
			}
		}
		this.toggleIgnoreRowsAboveHeader()
		this.refreshData(e)
	},

	/**
	 * Toggles the blue background for the selected header row
	 */
	toggleHeaderRowBackground: function(row, val) {
		row.removeClass('is-ignored')
		row.toggleClass('header-row', val)
	},

	/**
	 * Clears the display changes of previously selected header row:
	 * - any changes in formatting for yes-no values
	 * - removes blue background
	 */
	clearPreviousHeader: function () {
		let existingHeaderRows = this.$('tr.header-row')
		_.each(this.$(existingHeaderRows), row => {
			this.toggleYesNoFormattingForHeaderRow(row, false)
			this.$(row).removeClass('header-row')
		})
	},

	/**
	 * Toggles format display of header cells that have been parsed as yes-no
	 * data types, so that "Y" is not formatted as "Yes" in a header row
	 * (#33709)
	 */
	toggleYesNoFormattingForHeaderRow: function (row, unformatted) {
		_.each(this.$(row).find('td'), td => {
			// get cell data bound to node (data-preview-panel.js uses d3)
			let cell = d3.select(td).datum()
			if (cell && _.get(cell, 'dataType') === YES_NO) {
				let valueToDisplay = _.get(cell, 'rawValue')
				if (!unformatted) {
					valueToDisplay = formatValue({
						value: cell.value,
						dataType: cell.dataType
					})
				}
				this.$(td).text(valueToDisplay)
			}
		})
	},

	/**
	 * Sets rows above the selected header row as ignored
	 */
	toggleIgnoreRowsAboveHeader: function () {
		let headerIndex = this.$('tr.header-row').data('id')
		if (this.model instanceof Import) {
			// clear the ignore row styling for any existing ignored rows above
			// the selected header row and set disabled checkboxes as unchecked
			// and enabled
			// (using the disabled checkboxes to find these rows, so we don't
			// clear styling for any ignored rows after the selected header row)
			let disabledCheckboxes = this.$('input[type=checkbox]:disabled')
			_.each(disabledCheckboxes, (cb) => {
				let row = this.$(cb).closest('tr')
				row.removeClass('is-ignored')
				this.$(cb).attr('disabled', false).attr('checked', false)
			})
		} else {
			// for datasets, clear all ignore row styling
			this.$('tr').removeClass('is-ignored')
		}

		// if a header row is selected
		if (!_.isNil(headerIndex)) {
			let ignoreCheckbox = 'input[type=checkbox].use-row'
			_.each(this.$('tbody tr'), (row, index) => {
				// add is-ignored class to rows above the selected header row
				if (index < headerIndex) {
					this.$(row).addClass('is-ignored')
					// check the associated ignore row checkbox and disable it
					this.$(row).find(ignoreCheckbox)
						.attr('disabled', true)
						.attr('checked', true)
				} else {
					return
				}
			})
			// make sure the checkbox for the  selected header row is disabled
			// and unchecked
			this.$('tr.header-row').find(ignoreCheckbox)
				.attr('disabled', true)
				.attr('checked', false)
		}
	},

	context: function () {
		let source = i18n.get('common.database') // database as default
		if (this.model.isDatabase()) {
			source = i18n.get('common.database')
		} else if (this.model.isSpreadsheet()) {
			source = i18n.get('documents.fileType.spreadsheet')
		}
		let metadata = this.model.get('dataSourceMetadata')
		let title = i18n.get('admin.import.chooseWorksheet')
		if (_.size(metadata.sheets) < 2) {
			title = i18n.get('datasets.fields.create.selectHeaderRow')
		}

		let instructions = i18n.get('admin.import.worksheetOptions.instructions.selectColumnLabelsRow')
		if (this.model instanceof Import) {
			instructions += ' ' + i18n.get('admin.import.worksheetOptions.instructions.ignoreCheckboxes')
		}

		return {
			title,
			panelTitle: i18n.get('admin.import.exampleData', source),
			instructions
		}
	},

	// update preview data if selected worksheet was changed or "show all rows"
	// was clicked
	refreshData: function (e) {
		let sheetIndex = formHelper.readFromDom(this.el)?.sheetIndex
		// #54771 don't check the sheet index if refreshData was called by
		// clicking the "show all rows" link
		let isShowAll = this.isShowAllEvent(e)
		if (!isShowAll) {
			if (_.isNil(sheetIndex) || sheetIndex == '') {
				// #52705 filtering selects have their value cleared out as soon as
				// typing starts, and the value isn't set until a item is selected.
				// So if the sheetIndex is empty, just leave everything alone
				return
			}
		}

		this.saveState()
		this.resetForm()
		// #33078 prevent clicking on primary button while data is still loading
		this.formView.getPrimaryButton().loading()
		this.dataPreviewPanel.loading()
		let url = _.result(this.model, 'getSourceDataPreviewUrl')
		// #52000 prevent js error when refreshing data after toggling
		// "column labels" toggles
		if (isShowAll) {
			url += '/all'
		}
		// #36911 clear these out in case the user went back and selected
		// different options
		_.invoke(this.options.wizard, 'setNewColumnChooserIndex', null)
		_.invoke(this.options.wizard, 'setSubmitHandler', null)
		return this.getPreview(url, response => {
			this.model.set(response)
			this.dataSourceMetadata = response
			this.renderPreviewPanel()
			this.toggleShowAll()
			this.formView.getPrimaryButton().enable()
			this.trigger('resize')
		})
	},

	/**
	 * returns true if the event was triggered by clicking on the "show all" link
	 */
	isShowAllEvent(e) {
		return e?.target?.classList.contains('show-all')
	},

	/**
	 * Gets the preview data based on the users worksheet and header row selection.
	 * @param url
	 * @returns {JQuery.jqXHR}
	 */
	getPreview: function(url, success) {
		let fileId
		if (this.model.get('dataSourceMetadata').fileIds) {
			// #29607 When dealing with multiple files, remove the singular fileId so
			// it doesn't override the array
			fileId = this.model.attributes.dataSourceMetadata.fileId
			delete this.model.attributes.dataSourceMetadata.fileId
		}
		return this.previewXhr = $.ajax({
			method: 'post',
			data: JSON.stringify(this.model),
			url,
			success,
			error: (xhr) => {
				this.formView.error(xhr)
				this.formView.getPrimaryButton().disable()

				let renderBlankTable = false
				const response = _.get(xhr, 'responseJSON')
				const messageCode = _.get(response, 'messageCode')

				// #53470
				// Set the data for the preview panel so the user can see what
				// the table looks like that caused the error
				this.model.set(response)
				this.dataSourceMetadata = response

				// #34425 if there weren't any columns in the spreadsheet , clear the
				// table out and remove the loading dots
				if (messageCode === 'datasets.dataSource.spreadsheet.noColumns') {
					renderBlankTable = true
				}
				// #59477 even if there's an error, we want to return a preview
				// so that the user can see the data and potentially correct
				// any errors with the header row.
				const preview = renderBlankTable ? {} :
					_.get(response, 'preview')
				this.renderPreviewPanel(preview)

				// #43585 blow away header row index to reset validation
				delete this.model.attributes.dataSourceMetadata.headerRow
				// #43585 reset file Id to avoid issues with going back
				this.model.attributes.dataSourceMetadata.fileId = fileId
			}
		})
	},

	resetForm: function () {
		// #21881 remove validation message from previous sheet
		delete this.model.get('dataSourceMetadata').messageCode
		this.formView.clearErrors()
		// #24572 remove "show all" link from previous sheet
		delete this.model.get('dataSourceMetadata').displayShowAll
		this.toggleShowAll()
	},

	toggleShowAll: function () {
		let displayShowAll = this.model.get('dataSourceMetadata').displayShowAll
		this.$('.show-all-rows').toggleClass('is-not-displayed', !displayShowAll)
	},

	test: function () {
		// #31019 a header row is required for datasets
		let headerRowSelected = this.$('input[type="checkbox"]:checked').length > 0
		let headerRowRequired = this.model instanceof Dataset
		if (!headerRowSelected && headerRowRequired) {
			return i18n.get('admin.import.worksheetOptions.headerRow.required')
		}

		return this.model.get('dataSourceMetadata').messageCode
	},

	saveState: function () {
		const data = formHelper.readFromDom(this.el)
		// remove the entries for the header row toggles from the form data
		_.keys(data).forEach(key => {
			if (key.startsWith('toggle')) {
				delete data[key]
			}
		})

		// #32068 ignoreRows are still needed for Imports
		if (this.model instanceof Import) {
			let ignoreRows = []
			// get the rows the user does not want to use
			// #31019 this will be all rows prior to the selected header row
			// #32110 don't accidentally include the header row
			ignoreRows = _.map(this.$('tr.is-ignored:not(.header-row)'), row => {
				return this.$(row).data('id')
			})
			// #32146 don't wipe out any ignored rows that are not currently
			// being displayed in the example data
			let lastDisplayedRow = this.$('tr:last').data('id')
			let originalIgnoreRows = this.model.getDataSourceMetadata('ignoreRows')
			let hiddenIgnoreRows =
				_.filter(originalIgnoreRows, row => row > lastDisplayedRow) || []
			ignoreRows = _.concat(ignoreRows, hiddenIgnoreRows)
			data.ignoreRows = ignoreRows
		}

		// determine if a header row was selected
		data.headerRow = this.$('tr.header-row').data('id')
		data.header = !_.isNil(data.headerRow)

		_.extend(this.model.attributes.dataSourceMetadata, data)
	},

	close: function() {
		_.invokeMap([this.refetchXhr, this.previewXhr], 'abort')
	},


	submit: function () {
		this.saveState()
		if (this.options.wizard.isLastStep()) {
			let url = _.result(this.model, 'url')
			if (this.model instanceof Dataset) {
				url += '/data-source-info'
			}
			// #28185 clear import status when editing data source info
			if (this.model instanceof Import) {
				url += '/clear'
			}
			if (!_.isNil(this.options.replaceAllRecords)) {
				this.model.set('replaceAllRecords', this.options.replaceAllRecords)
			}
			this.model.save({}, {
				url,
				success: (model, responseData) => {
					// #37709 update the dataset connection's primary key in case we
					// mapped the primary key's column to a new column
					if (responseData.primaryKey) {
						const dataSourceMetadata = this.model.get('dataSourceMetadata')
						dataSourceMetadata.primaryKey = responseData.primaryKey
						model.set('dataSourceMetadata', dataSourceMetadata)
					}
					this.model = model
					this.trigger('done', this.model)
					this.options.wizard.finish(this.model)
				},
				error: (model, xhr) => {
					// #36911 this function displays a dialog for the user
					// to map missing columns to newly found columns, but
					// allows us to complete the save operation
					this.options.wizard.setSubmitHandler(columnMap => {
						this.model.set('newColumnNames', columnMap)
						this.submit()
					})
					this.options.wizard.setNewColumnChooserIndexLast()
					if (!handleMissingColumnsException(xhr, this.options)) {
						// #36911 make sure to hide the next step if we don't
						// have enough information to remap the columns
						this.options.wizard.setNewColumnChooserIndex(null)
						this.formView.error(xhr)
					}
				}
			})
		} else {
			// #31654 If this is a new Importable, if any rows were marked as
			// ignored, refresh the data before we continue on in the wizard
			// so that we have updated metadata and column mappings
			if (!_.isEmpty(this.model.getValidIgnoreRows())) {
				let url = _.result(this.model, 'getSourceDataPreviewUrl')
				return this.refetchXhr = $.ajax({
					method: 'post',
					data: JSON.stringify(this.model),
					url,
					success: response => {
						this.model.set(response)
						this.dataSourceMetadata = response
						this.options.wizard.next()
					}
				})
			}
			const url = _.result(this.model, 'getSourceDataPreviewUrl')
			return this.getPreview(url, response => {
				this.model.set(response)
				this.dataSourceMetadata = response
				this.options.wizard.next()
			})
		}
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.dataPreviewPanel, this.toggles,
			this.scrollingDialogTable, this.sheetIndexField], 'remove')
	}

})

export default WorksheetOptions
WorksheetOptions.__filename__ = 'imports/worksheet-options.js'
